import { useEffect, useRef, useState } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { useAuth } from "@/lib/auth"
import { apiClient } from "@/lib/api"
import { CopyConfigModal } from "@/components/CopyConfigModal"

// ---- Types ----

type StrictnessLevel = "relaxed" | "standard" | "strict"
type DeliveryLevel = "guidebook_only" | "light" | "standard" | "strict"
type PolicyValue = "not_allowed" | "allowed" | "approval_required"

interface Surfaces {
  guidebook: boolean
  display: boolean
}

/** Used by runtime display selection to increase prominence of rules when reservation.adults >= adultsAtLeast */
interface AdultsEmphasis {
  enabled: boolean
  adultsAtLeast: number // min 2, max 20
}

interface QuietHoursConfig {
  enabled: boolean
  startTime: string
  endTime: string
  strictness: StrictnessLevel
  surfaces: Surfaces
  delivery: DeliveryLevel
  adultsEmphasis?: AdultsEmphasis
}

interface PolicyConfig {
  enabled: boolean
  value: PolicyValue
  notes: string
  strictness: StrictnessLevel
  surfaces: Surfaces
  delivery: DeliveryLevel
  adultsEmphasis?: AdultsEmphasis
}

interface CheckoutConfig {
  enabled: boolean
  checkoutTime: string
  checklist: string[]
}

interface WasteConfig {
  enabled: boolean
  binLocation: string
  recyclingTypes: string[]
  notes: string
}

interface SafetyConfig {
  enabled: boolean
  enabledRules: string[]
  customRules: string[]
}

interface ParkingConfig {
  instruction: string
  notes: string
}

interface OutdoorConfig {
  rules: string[]
  notes: string
}

interface DisplayTimingConfig {
  showOnArrival: boolean
  showNightBeforeCheckout: boolean
  showDuringStayIfRelevant: boolean
}

interface CheckinConfig {
  enabled: boolean
  checkinTime: string
}

interface HouseRulesConfig {
  checkin: CheckinConfig
  quietHours: QuietHoursConfig
  parties: PolicyConfig
  smoking: PolicyConfig
  pets: PolicyConfig
  visitors: PolicyConfig
  checkout: CheckoutConfig
  waste: WasteConfig
  safety: SafetyConfig
  parking: ParkingConfig
  outdoor: OutdoorConfig
  custom: { rules: string[] }
  displayTiming: DisplayTimingConfig
}

// ---- Defaults ----

const DEFAULT_SURFACES: Surfaces = { guidebook: true, display: false }

const DEFAULT_RULES: HouseRulesConfig = {
  checkin: { enabled: true, checkinTime: "15:00" },
  quietHours: { enabled: true, startTime: "22:00", endTime: "07:00", strictness: "standard", surfaces: { ...DEFAULT_SURFACES }, delivery: "guidebook_only", adultsEmphasis: { enabled: false, adultsAtLeast: 6 } },
  parties: { enabled: true, value: "not_allowed", notes: "", strictness: "standard", surfaces: { ...DEFAULT_SURFACES }, delivery: "guidebook_only", adultsEmphasis: { enabled: false, adultsAtLeast: 6 } },
  smoking: { enabled: false, value: "not_allowed", notes: "", strictness: "strict", surfaces: { ...DEFAULT_SURFACES }, delivery: "guidebook_only" },
  pets: { enabled: false, value: "approval_required", notes: "", strictness: "standard", surfaces: { ...DEFAULT_SURFACES }, delivery: "guidebook_only" },
  visitors: { enabled: false, value: "allowed", notes: "", strictness: "relaxed", surfaces: { ...DEFAULT_SURFACES }, delivery: "guidebook_only" },
  checkout: {
    enabled: true,
    checkoutTime: "11:00",
    checklist: [
      "Strip beds and leave linen in bathroom",
      "Run dishwasher or hand-wash dishes",
      "Take out trash to bins",
      "Close and lock all windows",
      "Turn off all lights and appliances",
      "Return keys to lockbox",
    ],
  },
  waste: { enabled: true, binLocation: "", recyclingTypes: ["paper", "plastic", "glass", "metal"], notes: "" },
  safety: { enabled: false, enabledRules: ["no_candles", "no_fireworks", "lock_doors_at_night"], customRules: [] },
  parking: { instruction: "", notes: "" },
  outdoor: { rules: [], notes: "" },
  custom: { rules: [] },
  // UI removed — kept for backward compat
  displayTiming: { showOnArrival: true, showNightBeforeCheckout: true, showDuringStayIfRelevant: false },
}

const RECYCLING_OPTIONS = [
  { value: "paper", label: "Paper & Cardboard" },
  { value: "plastic", label: "Plastic" },
  { value: "glass", label: "Glass" },
  { value: "metal", label: "Metal & Cans" },
  { value: "organic", label: "Organic / Compost" },
  { value: "electronics", label: "Electronics" },
]

const SAFETY_PRESETS = [
  { value: "no_candles", label: "No open flames or candles" },
  { value: "no_fireworks", label: "No fireworks or pyrotechnics" },
  { value: "lock_doors_at_night", label: "Lock doors at night" },
  { value: "fire_extinguisher_location", label: "Fire extinguisher location noted" },
  { value: "emergency_numbers_posted", label: "Emergency numbers posted" },
]

const POLICY_LABELS: Record<PolicyValue, string> = {
  not_allowed: "Not allowed",
  allowed: "Allowed",
  approval_required: "Approval required",
}

const DELIVERY_SELECT_LABELS: Record<DeliveryLevel, string> = {
  guidebook_only: "Gbook only",
  light: "Light",
  standard: "Standard",
  strict: "Strict",
}

const POLICY_RULES = ["quietHours", "parties", "smoking", "pets", "visitors"] as const
type PolicyRuleKey = typeof POLICY_RULES[number]

const NOTES_PLACEHOLDERS: Record<string, string> = {
  parties: "Any exceptions or conditions...",
  smoking: "Where is smoking allowed? (e.g., balcony only)",
  pets: "Size limits, pet fee, etc.",
  visitors: "Visiting hours, max guests, etc.",
}

// ---- Helpers ----

function deliveryToStrictness(delivery: DeliveryLevel): StrictnessLevel {
  if (delivery === "standard") return "standard"
  if (delivery === "strict") return "strict"
  return "relaxed"
}

function deepMergeRules(defaults: HouseRulesConfig, loaded: Record<string, unknown>): HouseRulesConfig {
  const result = { ...defaults }
  for (const key of Object.keys(defaults) as Array<keyof HouseRulesConfig>) {
    if (key in loaded && loaded[key] != null) {
      const val = loaded[key]
      if (typeof val === "object" && !Array.isArray(val)) {
        result[key] = { ...(defaults[key] as Record<string, unknown>), ...(val as Record<string, unknown>) } as never
      } else {
        result[key] = val as never
      }
    }
  }

  // Backward compat: sections saved before per-rule enablement should default to enabled=true
  const enabledSections = ["quietHours", "parties", "smoking", "pets", "visitors", "checkout", "waste", "safety"] as const
  for (const sectionKey of enabledSections) {
    if (sectionKey in loaded && loaded[sectionKey] != null) {
      const raw = loaded[sectionKey] as Record<string, unknown>
      if (typeof raw.enabled !== "boolean") {
        ;(result[sectionKey] as unknown as Record<string, unknown>).enabled = true
      }
    }
  }

  // Migrate strictness → surfaces + delivery for rules that have delivery controls
  const deliverySections = ["quietHours", "parties", "smoking", "pets", "visitors"] as const
  for (const sectionKey of deliverySections) {
    const section = result[sectionKey] as unknown as Record<string, unknown>
    if (!section.surfaces || !section.delivery) {
      const strictness = (section.strictness as string) || "standard"
      if (strictness === "standard") {
        section.surfaces = { guidebook: true, display: true }
        section.delivery = "standard"
      } else if (strictness === "strict") {
        section.surfaces = { guidebook: true, display: true }
        section.delivery = "strict"
      } else {
        section.surfaces = { guidebook: true, display: false }
        section.delivery = "guidebook_only"
      }
    }
  }

  return result
}

function formatTime(iso: string): string {
  if (!iso) return ""
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
  } catch {
    return iso
  }
}

function getRuleSummary(section: keyof HouseRulesConfig, rules: HouseRulesConfig): string {
  switch (section) {
    case "checkin":
      return `At ${rules.checkin.checkinTime}`
    case "quietHours":
      return `${rules.quietHours.startTime} \u2192 ${rules.quietHours.endTime}`
    case "parties":
      return POLICY_LABELS[rules.parties.value]
    case "smoking":
      return POLICY_LABELS[rules.smoking.value]
    case "pets":
      return POLICY_LABELS[rules.pets.value]
    case "visitors":
      return POLICY_LABELS[rules.visitors.value]
    case "checkout":
      return `By ${rules.checkout.checkoutTime}`
    case "waste": {
      const n = rules.waste.recyclingTypes.length
      return n > 0 ? `${n} recycling type${n !== 1 ? "s" : ""}` : ""
    }
    case "safety": {
      const n = rules.safety.enabledRules.length + rules.safety.customRules.length
      return n > 0 ? `${n} rule${n !== 1 ? "s" : ""} active` : ""
    }
    default:
      return ""
  }
}

// ---- Page ----

export function HouseRulesPage() {
  const { propertyId, property } = usePropertyContext()
  const { properties } = useAuth()
  const [rules, setRules] = useState<HouseRulesConfig>(DEFAULT_RULES)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [lastSavedAt, setLastSavedAt] = useState("")
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rulesRef = useRef(rules)

  // New state for table layout
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  useEffect(() => {
    rulesRef.current = rules
  }, [rules])

  // Collapse expanded row if its rule becomes disabled
  useEffect(() => {
    if (expandedRow) {
      const rule = rules[expandedRow as keyof HouseRulesConfig] as { enabled?: boolean } | undefined
      if (rule && rule.enabled === false) setExpandedRow(null)
    }
  }, [rules, expandedRow])

  // Fetch on mount
  useEffect(() => {
    if (!propertyId) return
    setLoading(true)
    apiClient.getHouseRules(propertyId).then((res) => {
      if (res.ok && res.rules) {
        const merged = deepMergeRules(DEFAULT_RULES, res.rules)
        setRules(merged)
        if (res.updated_at) setLastSavedAt(res.updated_at)
      }
      setLoading(false)
    })
  }, [propertyId])

  function updateRules<K extends keyof HouseRulesConfig>(
    section: K,
    updater: (prev: HouseRulesConfig[K]) => HouseRulesConfig[K]
  ) {
    setRules((prev) => ({ ...prev, [section]: updater(prev[section]) }))
    setDirty(true)
    setSuccess("")
    setError("")
  }

  // Per-rule delivery change
  function handleRuleDeliveryChange(key: PolicyRuleKey, next: DeliveryLevel) {
    updateRules(key, (prev) => ({
      ...prev,
      delivery: next,
      strictness: deliveryToStrictness(next),
    }))
  }

  // Per-rule channel override (from detail panel)
  function handleRuleSurfacesChange(key: PolicyRuleKey, next: Surfaces) {
    updateRules(key, (prev) => {
      const rule = prev as PolicyConfig | QuietHoursConfig
      let delivery = rule.delivery
      if (!next.display && (delivery === "standard" || delivery === "strict")) {
        delivery = "light"
      }
      return { ...rule, surfaces: next, delivery, strictness: deliveryToStrictness(delivery) } as typeof prev
    })
  }

  function handleToggleExpand(key: string) {
    setExpandedRow((prev) => (prev === key ? null : key))
  }

  // Autosave
  useEffect(() => {
    if (!dirty || !propertyId) return
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(() => {
      performSave()
    }, 1500)
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    }
  }, [dirty, rules]) // eslint-disable-line react-hooks/exhaustive-deps

  async function performSave() {
    if (!propertyId || saving) return
    setSaving(true)
    setError("")
    const res = await apiClient.saveHouseRules(
      rulesRef.current as unknown as Record<string, unknown>,
      propertyId,
    )
    if (res.ok) {
      setDirty(false)
      setSuccess("Saved")
      if (res.updated_at) setLastSavedAt(res.updated_at)
      if (res.rules) setRules(deepMergeRules(DEFAULT_RULES, res.rules))
      setTimeout(() => setSuccess(""), 2000)
    } else {
      setError(res.error || "Failed to save")
    }
    setSaving(false)
  }

  function handleManualSave() {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    performSave()
  }

  if (loading) {
    return (
      <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
        <div className="sf-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header__row">
          <div>
            <h1 className="page-header__title">House Rules</h1>
            <p className="page-header__subtitle">
              Keep this short. Guests will see these at the right moment.
            </p>
          </div>
          <div className="page-header__actions">
            {properties.length > 1 && (
              <button
                className="sf-btn sf-btn--ghost sf-btn--sm"
                onClick={() => setShowCopyModal(true)}
                type="button"
              >
                Copy to...
              </button>
            )}
            <SaveIndicator saving={saving} success={success} lastSavedAt={lastSavedAt} />
          </div>
        </div>
      </div>

      {error && (
        <div
          className="login-card__error"
          role="alert"
          style={{ marginBottom: "var(--sf-space-4)" }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--sf-space-2)" }}>

        {/* ── Arrival ── */}
        <div className="hr-section-group">
          <h3 className="hr-section-group__title">Arrival</h3>
          <p className="hr-section-group__desc">Check-in details for guests</p>
        </div>
        <div className="hr-policy-table-wrap">
          <table className="hr-policy-table">
            <thead>
              <tr>
                <th style={{ width: 52 }}></th>
                <th>Rule</th>
                <th style={{ width: 180 }}>Policy</th>
              </tr>
            </thead>
            <tbody>
              <PolicyRow
                ruleKey="checkin"
                label="Check-in"
                enabled={rules.checkin.enabled}
                onEnabledChange={(v) => updateRules("checkin", (prev) => ({ ...prev, enabled: v }))}
                summary={getRuleSummary("checkin", rules)}
                isExpanded={expandedRow === "checkin"}
                onToggleExpand={() => handleToggleExpand("checkin")}
                colSpan={3}
              >
                <div className="hr-row-detail__time-row">
                  <label className="hr-row-detail-section__label" htmlFor="hr-checkin-time">
                    Check-in time
                  </label>
                  <input
                    id="hr-checkin-time"
                    type="time"
                    className="login-card__input"
                    value={rules.checkin.checkinTime}
                    onChange={(e) =>
                      updateRules("checkin", (prev) => ({ ...prev, checkinTime: e.target.value }))
                    }
                    style={{ height: 34, fontSize: "var(--sf-text-sm)", width: 120 }}
                  />
                </div>
              </PolicyRow>
            </tbody>
          </table>
        </div>

        {/* ── Stay Basics ── */}
        <div className="hr-section-group">
          <h3 className="hr-section-group__title">Stay Basics</h3>
          <p className="hr-section-group__desc">Rules guests should know during their stay</p>
        </div>
        <div className="hr-policy-table-wrap">
          <table className="hr-policy-table">
            <thead>
              <tr>
                <th style={{ width: 52 }}></th>
                <th>Rule</th>
                <th style={{ width: 160 }}>Policy</th>
                <th style={{ width: 140 }}>Delivery</th>
              </tr>
            </thead>
            <tbody>
              {/* Quiet Hours */}
              <PolicyRow
                ruleKey="quietHours"
                label="Quiet Hours"
                enabled={rules.quietHours.enabled}
                onEnabledChange={(v) => updateRules("quietHours", (prev) => ({ ...prev, enabled: v }))}
                summary={getRuleSummary("quietHours", rules)}
                delivery={rules.quietHours.delivery}
                onDeliveryChange={(v) => handleRuleDeliveryChange("quietHours", v)}
                displayEnabled={rules.quietHours.surfaces.display}
                isExpanded={expandedRow === "quietHours"}
                onToggleExpand={() => handleToggleExpand("quietHours")}
                colSpan={4}
              >
                <div className="hr-row-detail-section">
                  <span className="hr-row-detail-section__label">Time range</span>
                  <div className="hr-row-detail__time-row">
                    <input
                      type="time"
                      className="login-card__input"
                      value={rules.quietHours.startTime}
                      onChange={(e) =>
                        updateRules("quietHours", (prev) => ({ ...prev, startTime: e.target.value }))
                      }
                      style={{ height: 34, fontSize: "var(--sf-text-sm)", width: 120 }}
                    />
                    <span style={{ color: "var(--sf-text-muted)", fontSize: "var(--sf-text-sm)" }}>to</span>
                    <input
                      type="time"
                      className="login-card__input"
                      value={rules.quietHours.endTime}
                      onChange={(e) =>
                        updateRules("quietHours", (prev) => ({ ...prev, endTime: e.target.value }))
                      }
                      style={{ height: 34, fontSize: "var(--sf-text-sm)", width: 120 }}
                    />
                  </div>
                </div>
                <ChannelOverride
                  ruleKey="quietHours"
                  surfaces={rules.quietHours.surfaces}
                  onChange={(s) => handleRuleSurfacesChange("quietHours", s)}
                />
                {rules.quietHours.surfaces.display && (rules.quietHours.delivery === "standard" || rules.quietHours.delivery === "strict") && (
                  <AdultsEmphasisControl
                    id="hr-quiet"
                    value={rules.quietHours.adultsEmphasis}
                    onChange={(ae) => updateRules("quietHours", (prev) => ({ ...prev, adultsEmphasis: ae }))}
                  />
                )}
              </PolicyRow>

              {/* Parties, Smoking, Pets, Visitors */}
              {(["parties", "smoking", "pets", "visitors"] as const).map((key) => {
                const rule = rules[key]
                return (
                  <PolicyRow
                    key={key}
                    ruleKey={key}
                    label={key === "parties" ? "Parties & Events" : key.charAt(0).toUpperCase() + key.slice(1)}
                    enabled={rule.enabled}
                    onEnabledChange={(v) => updateRules(key, (prev) => ({ ...prev, enabled: v }))}
                    summary={getRuleSummary(key, rules)}
                    delivery={rule.delivery}
                    onDeliveryChange={(v) => handleRuleDeliveryChange(key, v)}
                    displayEnabled={rule.surfaces.display}
                    isExpanded={expandedRow === key}
                    onToggleExpand={() => handleToggleExpand(key)}
                    colSpan={4}
                  >
                    <PolicyRadioGroup
                      value={rule.value}
                      onChange={(v) => updateRules(key, (prev) => ({ ...prev, value: v }))}
                    />
                    {rule.value !== "not_allowed" && (
                      <textarea
                        className="login-card__input"
                        value={rule.notes}
                        onChange={(e) => updateRules(key, (prev) => ({ ...prev, notes: e.target.value }))}
                        placeholder={NOTES_PLACEHOLDERS[key] || "Additional notes..."}
                        rows={2}
                        maxLength={200}
                        style={{ fontSize: "var(--sf-text-sm)", resize: "vertical", width: "100%", minHeight: 56 }}
                      />
                    )}
                    <ChannelOverride
                      ruleKey={key}
                      surfaces={rule.surfaces}
                      onChange={(s) => handleRuleSurfacesChange(key, s)}
                    />
                    {rule.surfaces.display && (rule.delivery === "standard" || rule.delivery === "strict") && (
                      <AdultsEmphasisControl
                        id={`hr-${key}`}
                        value={rule.adultsEmphasis}
                        onChange={(ae) => updateRules(key, (prev) => ({ ...prev, adultsEmphasis: ae }))}
                      />
                    )}
                  </PolicyRow>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="hr-delivery-footnote">
          <strong>Delivery levels</strong>
          <dl className="hr-delivery-legend">
            <div className="hr-delivery-legend__item">
              <dt>Gbook only</dt>
              <dd>Shown in the guest guidebook. Not mentioned on the display.</dd>
            </div>
            <div className="hr-delivery-legend__item">
              <dt>Light</dt>
              <dd>Guidebook + display mentions it casually if relevant.</dd>
            </div>
            <div className="hr-delivery-legend__item">
              <dt>Standard</dt>
              <dd>Guidebook + display weaves it into guest messages.</dd>
            </div>
            <div className="hr-delivery-legend__item">
              <dt>Strict</dt>
              <dd>Guidebook + display emphasises this rule prominently.</dd>
            </div>
          </dl>
        </div>

        {/* ── Departure ── */}
        <div className="hr-section-group">
          <h3 className="hr-section-group__title">Departure</h3>
          <p className="hr-section-group__desc">Checkout process and tasks for guests</p>
        </div>
        <div className="hr-policy-table-wrap">
          <table className="hr-policy-table">
            <thead>
              <tr>
                <th style={{ width: 52 }}></th>
                <th>Rule</th>
                <th style={{ width: 180 }}>Policy</th>
              </tr>
            </thead>
            <tbody>
              <PolicyRow
                ruleKey="checkout"
                label="Checkout"
                enabled={rules.checkout.enabled}
                onEnabledChange={(v) => updateRules("checkout", (prev) => ({ ...prev, enabled: v }))}
                summary={getRuleSummary("checkout", rules)}
                isExpanded={expandedRow === "checkout"}
                onToggleExpand={() => handleToggleExpand("checkout")}
                colSpan={3}
              >
                <div className="hr-row-detail-section">
                  <span className="hr-row-detail-section__label">Checkout time</span>
                  <input
                    id="hr-checkout-time"
                    type="time"
                    className="login-card__input"
                    value={rules.checkout.checkoutTime}
                    onChange={(e) =>
                      updateRules("checkout", (prev) => ({ ...prev, checkoutTime: e.target.value }))
                    }
                    style={{ height: 34, fontSize: "var(--sf-text-sm)", width: 120 }}
                  />
                </div>
                <div className="hr-row-detail-section">
                  <span className="hr-row-detail-section__label">Checkout checklist</span>
                  <div style={{ fontSize: "var(--sf-text-xs)", color: "var(--sf-text-muted)", marginBottom: "var(--sf-space-1)" }}>
                    Shown to guests the night before checkout. Keep it under 5 items for clarity.
                  </div>
                  <ChecklistEditor
                    items={rules.checkout.checklist}
                    onChange={(items) => updateRules("checkout", (prev) => ({ ...prev, checklist: items }))}
                    maxItems={8}
                    placeholder="Add checkout task..."
                  />
                </div>
              </PolicyRow>

              <PolicyRow
                ruleKey="waste"
                label="Waste & Recycling"
                enabled={rules.waste.enabled}
                onEnabledChange={(v) => updateRules("waste", (prev) => ({ ...prev, enabled: v }))}
                summary={getRuleSummary("waste", rules)}
                isExpanded={expandedRow === "waste"}
                onToggleExpand={() => handleToggleExpand("waste")}
                colSpan={3}
              >
                <div className="hr-row-detail-section">
                  <label className="hr-row-detail-section__label" htmlFor="hr-bin-location">
                    Bin location
                  </label>
                  <input
                    id="hr-bin-location"
                    className="login-card__input"
                    type="text"
                    value={rules.waste.binLocation}
                    onChange={(e) =>
                      updateRules("waste", (prev) => ({ ...prev, binLocation: e.target.value }))
                    }
                    placeholder="e.g., Behind the garage, side entrance"
                    maxLength={200}
                    style={{ height: 34, fontSize: "var(--sf-text-sm)", width: "100%" }}
                  />
                </div>
                <div className="hr-row-detail-section">
                  <span className="hr-row-detail-section__label">Recycling types accepted</span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sf-space-2)" }}>
                    {RECYCLING_OPTIONS.map((opt) => {
                      const active = rules.waste.recyclingTypes.includes(opt.value)
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`hr-policy-radio__btn${active ? " hr-policy-radio__btn--active" : ""}`}
                          onClick={() =>
                            updateRules("waste", (prev) => ({
                              ...prev,
                              recyclingTypes: prev.recyclingTypes.includes(opt.value)
                                ? prev.recyclingTypes.filter((t) => t !== opt.value)
                                : [...prev.recyclingTypes, opt.value],
                            }))
                          }
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div className="hr-row-detail-section">
                  <label className="hr-row-detail-section__label" htmlFor="hr-waste-notes">
                    Additional notes
                  </label>
                  <textarea
                    id="hr-waste-notes"
                    className="login-card__input"
                    value={rules.waste.notes}
                    onChange={(e) => updateRules("waste", (prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="e.g., Collection day is Tuesday morning"
                    rows={2}
                    maxLength={200}
                    style={{ fontSize: "var(--sf-text-sm)", resize: "vertical", width: "100%", minHeight: 56 }}
                  />
                </div>
              </PolicyRow>

              <PolicyRow
                ruleKey="safety"
                label="Safety & Property Care"
                enabled={rules.safety.enabled}
                onEnabledChange={(v) => updateRules("safety", (prev) => ({ ...prev, enabled: v }))}
                summary={getRuleSummary("safety", rules)}
                isExpanded={expandedRow === "safety"}
                onToggleExpand={() => handleToggleExpand("safety")}
                colSpan={3}
              >
                <div className="hr-row-detail-section">
                  <span className="hr-row-detail-section__label">Safety rules</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--sf-space-2)" }}>
                    {SAFETY_PRESETS.map((preset) => (
                      <label
                        key={preset.value}
                        style={{ display: "flex", alignItems: "center", gap: "var(--sf-space-2)", cursor: "pointer", fontSize: "var(--sf-text-sm)" }}
                      >
                        <input
                          type="checkbox"
                          checked={rules.safety.enabledRules.includes(preset.value)}
                          onChange={() =>
                            updateRules("safety", (prev) => ({
                              ...prev,
                              enabledRules: prev.enabledRules.includes(preset.value)
                                ? prev.enabledRules.filter((r) => r !== preset.value)
                                : [...prev.enabledRules, preset.value],
                            }))
                          }
                          style={{ width: 16, height: 16, accentColor: "var(--sf-primary)" }}
                        />
                        {preset.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="hr-row-detail-section">
                  <span className="hr-row-detail-section__label">Custom safety rules</span>
                  <ChecklistEditor
                    items={rules.safety.customRules}
                    onChange={(items) =>
                      updateRules("safety", (prev) => ({ ...prev, customRules: items }))
                    }
                    maxItems={4}
                    placeholder="Add custom safety rule..."
                  />
                </div>
              </PolicyRow>
            </tbody>
          </table>
        </div>

        {/* ── Optional Rules ── */}
        <OptionalRulesCard rules={rules} updateRules={updateRules} />
      </div>

      <CopyConfigModal
        isOpen={showCopyModal}
        sourcePropertyId={propertyId}
        sourcePropertyName={property?.name || "Current property"}
        configType="house-rules"
        properties={properties.map((p) => ({ id: p.id, name: p.name }))}
        onClose={() => setShowCopyModal(false)}
      />

      {dirty && (
        <div className="unsaved-bar">
          <span>{saving ? "Saving..." : "Unsaved changes"}</span>
          <div className="unsaved-bar__actions">
            <button
              className="data-table__action"
              onClick={() => window.location.reload()}
              type="button"
            >
              Discard
            </button>
            <button
              className="login-card__submit"
              style={{ width: "auto", padding: "0 var(--sf-space-5)", height: 36 }}
              onClick={handleManualSave}
              disabled={saving}
              type="button"
            >
              {saving ? "Saving..." : "Save Now"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Save Indicator ----

function SaveIndicator({
  saving,
  success,
  lastSavedAt,
}: {
  saving: boolean
  success: string
  lastSavedAt: string
}) {
  if (saving) {
    return (
      <div className="hr-save-indicator hr-save-indicator--saving">
        <div className="sf-spinner" style={{ width: 14, height: 14 }} />
        Saving...
      </div>
    )
  }
  if (success) {
    return <div className="hr-save-indicator hr-save-indicator--success">Saved</div>
  }
  if (lastSavedAt) {
    return (
      <div className="hr-save-indicator">Last saved {formatTime(lastSavedAt)}</div>
    )
  }
  return null
}

// ---- Policy Row ----

function PolicyRow({
  ruleKey,
  label,
  enabled,
  onEnabledChange,
  summary,
  delivery,
  onDeliveryChange,
  displayEnabled,
  isExpanded,
  onToggleExpand,
  colSpan,
  children,
}: {
  ruleKey: string
  label: string
  enabled: boolean
  onEnabledChange: (v: boolean) => void
  summary: string
  delivery?: DeliveryLevel
  onDeliveryChange?: (v: DeliveryLevel) => void
  displayEnabled?: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  colSpan: number
  children: React.ReactNode
}) {
  const hasDelivery = delivery !== undefined && onDeliveryChange !== undefined

  return (
    <>
      <tr
        className={[
          "hr-policy-row",
          !enabled && "hr-policy-row--disabled",
          isExpanded && "hr-policy-row--expanded",
        ].filter(Boolean).join(" ")}
        onClick={enabled ? onToggleExpand : undefined}
        onKeyDown={enabled ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onToggleExpand()
          }
        } : undefined}
        tabIndex={enabled ? 0 : -1}
        role={enabled ? "button" : undefined}
        aria-expanded={enabled ? isExpanded : undefined}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
        <td className="hr-policy-row__toggle" onClick={(e) => e.stopPropagation()}>
          <ToggleSwitch id={`hr-enable-${ruleKey}`} label="" checked={enabled} onChange={onEnabledChange} />
        </td>
        <td className="hr-policy-row__name">{label}</td>
        <td className="hr-policy-row__policy">
          {enabled && summary ? summary : <span className="hr-policy-row__empty">&mdash;</span>}
        </td>
        {hasDelivery && (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
          <td className="hr-policy-row__delivery-cell" onClick={(e) => e.stopPropagation()}>
            {enabled ? (
              <DeliverySelect
                value={delivery}
                displayEnabled={displayEnabled}
                onChange={onDeliveryChange}
              />
            ) : (
              <span className="hr-policy-row__empty">&mdash;</span>
            )}
          </td>
        )}
      </tr>
      {isExpanded && (
        <tr className="hr-policy-row__detail-row">
          <td colSpan={colSpan}>
            <div className="hr-policy-row__detail">
              {children}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ---- Delivery Select ----

function DeliverySelect({
  value,
  displayEnabled,
  onChange,
}: {
  value: DeliveryLevel
  displayEnabled?: boolean
  onChange: (v: DeliveryLevel) => void
}) {
  const levels: DeliveryLevel[] = ["guidebook_only", "light", "standard", "strict"]
  return (
    <select
      className="hr-delivery-select"
      value={value}
      onChange={(e) => onChange(e.target.value as DeliveryLevel)}
    >
      {levels.map((level) => (
        <option
          key={level}
          value={level}
          disabled={!displayEnabled && (level === "standard" || level === "strict")}
        >
          {DELIVERY_SELECT_LABELS[level]}
        </option>
      ))}
    </select>
  )
}

// ---- Channel Override (in detail panel) ----

function ChannelOverride({
  ruleKey,
  surfaces,
  onChange,
}: {
  ruleKey: string
  surfaces: Surfaces
  onChange: (s: Surfaces) => void
}) {
  return (
    <div className="hr-row-detail-section">
      <span className="hr-row-detail-section__label">Channels</span>
      <div className="hr-row-detail__channel-row">
        <label className="hr-row-detail__channel-label">
          <input
            type="checkbox"
            checked={surfaces.guidebook}
            onChange={() => {
              if (surfaces.guidebook) {
                onChange({ guidebook: false, display: false })
              } else {
                onChange({ ...surfaces, guidebook: true })
              }
            }}
          />
          Guest guidebook
        </label>
        <label className="hr-row-detail__channel-label">
          <input
            type="checkbox"
            checked={surfaces.display}
            onChange={() => {
              if (surfaces.display) {
                onChange({ ...surfaces, display: false })
              } else {
                onChange({ guidebook: true, display: true })
              }
            }}
          />
          In-home display
        </label>
      </div>
    </div>
  )
}

// ---- Shared Components ----

function PolicyRadioGroup({
  value,
  onChange,
}: {
  value: PolicyValue
  onChange: (v: PolicyValue) => void
}) {
  const options: { value: PolicyValue; label: string }[] = [
    { value: "not_allowed", label: "Not allowed" },
    { value: "allowed", label: "Allowed" },
    { value: "approval_required", label: "Approval required" },
  ]
  return (
    <div className="hr-policy-radio">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`hr-policy-radio__btn${value === opt.value ? " hr-policy-radio__btn--active" : ""}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function ChecklistEditor({
  items,
  onChange,
  maxItems = 8,
  placeholder = "Add item...",
}: {
  items: string[]
  onChange: (items: string[]) => void
  maxItems?: number
  placeholder?: string
}) {
  const [newItem, setNewItem] = useState("")

  function addItem() {
    const trimmed = newItem.trim()
    if (!trimmed || items.length >= maxItems) return
    onChange([...items, trimmed])
    setNewItem("")
  }

  function removeItem(index: number) {
    onChange(items.filter((_, i) => i !== index))
  }

  function moveItem(index: number, direction: -1 | 1) {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= items.length) return
    const next = [...items]
    ;[next[index], next[newIndex]] = [next[newIndex], next[index]]
    onChange(next)
  }

  return (
    <div className="hr-checklist">
      {items.map((item, i) => (
        <div key={i} className="hr-checklist__item">
          <span className="hr-checklist__text">{item}</span>
          <div className="hr-checklist__actions">
            <button
              type="button"
              disabled={i === 0}
              onClick={() => moveItem(i, -1)}
              title="Move up"
            >
              &uarr;
            </button>
            <button
              type="button"
              disabled={i === items.length - 1}
              onClick={() => moveItem(i, 1)}
              title="Move down"
            >
              &darr;
            </button>
            <button
              type="button"
              onClick={() => removeItem(i)}
              title="Remove"
              className="hr-checklist__remove"
            >
              &times;
            </button>
          </div>
        </div>
      ))}
      {items.length < maxItems && (
        <div className="hr-checklist__add">
          <input
            className="login-card__input"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                addItem()
              }
            }}
            style={{ height: 34, fontSize: "var(--sf-text-sm)", flex: 1 }}
          />
          <button
            type="button"
            className="data-table__action"
            onClick={addItem}
            style={{ height: 34 }}
          >
            Add
          </button>
        </div>
      )}
      <div
        style={{
          fontSize: "var(--sf-text-xs)",
          color: "var(--sf-text-muted)",
          marginTop: "var(--sf-space-1)",
        }}
      >
        {items.length}/{maxItems} items
      </div>
    </div>
  )
}

function ToggleSwitch({
  checked,
  onChange,
  label,
  id,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  id: string
}) {
  return (
    <label className="hr-toggle" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="hr-toggle__input"
      />
      <span className="hr-toggle__track">
        <span className="hr-toggle__thumb" />
      </span>
      {label && <span className="hr-toggle__label">{label}</span>}
    </label>
  )
}

function AdultsEmphasisControl({
  value,
  onChange,
  id,
}: {
  value: AdultsEmphasis | undefined
  onChange: (next: AdultsEmphasis) => void
  id: string
}) {
  const emphasis = value ?? { enabled: false, adultsAtLeast: 6 }

  function handleToggle(enabled: boolean) {
    onChange({ ...emphasis, enabled, adultsAtLeast: emphasis.adultsAtLeast || 6 })
  }

  function handleThresholdChange(raw: string) {
    const num = parseInt(raw, 10)
    if (isNaN(num)) return
    onChange({ ...emphasis, adultsAtLeast: Math.max(2, Math.min(20, num)) })
  }

  return (
    <div className="hr-emphasis">
      <div className="hr-emphasis__header">
        <span className="hr-emphasis__label">Emphasis for larger groups</span>
        <ToggleSwitch
          id={`${id}-emphasis-toggle`}
          label=""
          checked={emphasis.enabled}
          onChange={handleToggle}
        />
      </div>
      {emphasis.enabled && (
        <div className="hr-emphasis__controls">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--sf-space-2)", flexWrap: "wrap" }}>
            <span style={{ fontSize: "var(--sf-text-sm)", color: "var(--sf-text-secondary)" }}>
              If adults are at least
            </span>
            <input
              type="number"
              className="login-card__input"
              value={emphasis.adultsAtLeast}
              onChange={(e) => handleThresholdChange(e.target.value)}
              min={2}
              max={20}
              style={{ height: 32, fontSize: "var(--sf-text-sm)", width: 64, textAlign: "center" }}
            />
            <span style={{ fontSize: "var(--sf-text-sm)", color: "var(--sf-text-secondary)" }}>
              emphasise this rule
            </span>
          </div>
          <div className="hr-emphasis__chip">
            Emphasised when adults &ge; {emphasis.adultsAtLeast}
          </div>
          <div className="hr-emphasis__hint">
            Recommended: 4&ndash;8 adults. Larger adult groups are more likely to create noise.
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Optional Rules Card ----

interface SectionProps {
  rules: HouseRulesConfig
  updateRules: <K extends keyof HouseRulesConfig>(
    section: K,
    updater: (prev: HouseRulesConfig[K]) => HouseRulesConfig[K]
  ) => void
}

function OptionalRulesCard({ rules, updateRules }: SectionProps) {
  return (
    <div className="section-card">
      <h2 className="section-card__title">Optional Rules</h2>
      <p
        style={{
          fontSize: "var(--sf-text-sm)",
          color: "var(--sf-text-muted)",
          marginBottom: "var(--sf-space-4)",
          marginTop: 0,
        }}
      >
        Additional rules guests may need depending on your property.
      </p>

      {/* Parking */}
      <div style={{ marginBottom: "var(--sf-space-4)" }}>
        <label className="login-card__label" htmlFor="hr-parking">
          Parking instructions
        </label>
        <textarea
          id="hr-parking"
          className="login-card__input"
          value={rules.parking.instruction}
          onChange={(e) =>
            updateRules("parking", (prev) => ({
              ...prev,
              instruction: e.target.value,
            }))
          }
          placeholder="e.g., Park in spot #12 behind the building. Visitor permits in the welcome folder."
          rows={2}
          maxLength={300}
          style={{
            fontSize: "var(--sf-text-sm)",
            resize: "vertical",
            width: "100%",
            minHeight: 56,
          }}
        />
      </div>

      {/* Outdoor rules */}
      <div style={{ marginBottom: "var(--sf-space-4)" }}>
        <label className="login-card__label">Outdoor & garden rules</label>
        <ChecklistEditor
          items={rules.outdoor.rules}
          onChange={(items) =>
            updateRules("outdoor", (prev) => ({ ...prev, rules: items }))
          }
          maxItems={4}
          placeholder="Add outdoor rule..."
        />
      </div>

      {/* Custom special rules */}
      <div>
        <label className="login-card__label">Other special rules</label>
        <ChecklistEditor
          items={rules.custom.rules}
          onChange={(items) =>
            updateRules("custom", () => ({ rules: items }))
          }
          maxItems={4}
          placeholder="Add custom rule..."
        />
      </div>
    </div>
  )
}
